import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import {
  authorizationV2ClientsCreateMutation,
  authorizationV2ClientsListOptions,
  authorizationV2ClientsPartialUpdateMutation,
  authorizationV2ClientsRevokeCreateMutation,
  authorizationV2ClientsRotateCreateMutation
} from 'src/api/main'

import type { OidcClient } from './types'

type UseOidcClientArgs = {
  applicationId: number
  enabled: boolean
  onSecretRevealed: (secret: string) => void
  onSecretCleared: () => void
}

/**
 * Centralises every query / mutation for one application's OIDC client.
 * Components receive a ready-to-use facade and stay free of api wiring.
 */
export const useOidcClient = ({
  applicationId,
  enabled,
  onSecretRevealed,
  onSecretCleared
}: UseOidcClientArgs) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()

  const clientsQueryOptions = authorizationV2ClientsListOptions({
    query: { application: applicationId } as never
  })

  const {
    data: oidcClient,
    isLoading,
    isError,
    error: queryError,
    refetch
  } = useQuery({
    ...clientsQueryOptions,
    enabled: !!applicationId && enabled,
    select: (response) => {
      const paginated = response as unknown as {
        results?: Array<typeof response>
      }
      if (Array.isArray(paginated?.results)) {
        return (paginated.results[0] ?? null) as OidcClient | null
      }
      return ((response as typeof response | null) ?? null) as OidcClient | null
    }
  })

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: clientsQueryOptions.queryKey
      }),
    [clientsQueryOptions.queryKey, queryClient]
  )

  const captureSecret = useCallback(
    (client: unknown) => {
      const secret = (client as { client_secret?: string })?.client_secret
      if (secret) onSecretRevealed(secret)
    },
    [onSecretRevealed]
  )

  const { mutate: createClient, isPending: isCreating } = useMutation({
    ...authorizationV2ClientsCreateMutation(),
    onSuccess: (client) => {
      captureSecret(client)
      void invalidate()
      message.success(t('oidc.client_created', 'OIDC client created'))
    }
  })

  const { mutate: updateClient, isPending: isUpdating } = useMutation({
    ...authorizationV2ClientsPartialUpdateMutation(),
    onSuccess: (client) => {
      // A type swap from public to confidential mints a fresh client_secret;
      // surface it once just like the create endpoint does.
      captureSecret(client)
      void invalidate()
      message.success(t('oidc.client_updated', 'OIDC client updated'))
    }
  })

  const { mutate: rotateClientSecret, isPending: isRotating } = useMutation({
    ...authorizationV2ClientsRotateCreateMutation(),
    onSuccess: (client) => {
      captureSecret(client)
      void invalidate()
    }
  })

  const { mutate: revokeClient, isPending: isRevoking } = useMutation({
    ...authorizationV2ClientsRevokeCreateMutation(),
    onSuccess: () => {
      onSecretCleared()
      void invalidate()
      message.success(t('oidc.client_disabled', 'OIDC disabled'))
    }
  })

  return {
    oidcClient,
    isLoading,
    isError,
    queryError,
    refetch,
    createClient,
    isCreating,
    updateClient,
    isUpdating,
    rotateClientSecret,
    isRotating,
    revokeClient,
    isRevoking,
    invalidate
  }
}
